import m, { Vnode, VnodeDOM } from 'mithril';
import { FilterOption, FilterGroup, FilterDisplayMode } from '../FilterSelectDropdown';
import Translator from '../../../Translator';

export interface DropdownListHost {
    options: FilterOption[];
    searchQuery: string;
    highlightedIndex: number;
    translator: Translator;
    displayMode?: FilterDisplayMode;
    toggle(option: FilterOption): void;
    setSearchQuery(value: string): void;
    showSearch(): boolean;
    getFilteredOptions(): FilterOption[];
    getFilteredGroups?(): FilterGroup[] | null;
    getNavigableOptions(): FilterOption[];
    moveHighlight(delta: number): void;
}

export default class FilterSelectDropdownView {

    private itemsContainer: HTMLElement | null = null;

    constructor(
        private readonly host: DropdownListHost,
    ) { }

    public view(): Vnode[] {
        const nodes: Vnode[] = [];

        if (this.host.showSearch()) {
            nodes.push(this.search());
        }

        const groups = this.host.getFilteredGroups?.() ?? null;
        if (groups !== null) {
            nodes.push(this.groupedList(groups));
        } else {
            nodes.push(this.flatList(this.host.getFilteredOptions()));
        }

        return nodes;
    }

    private flatList(options: FilterOption[]): Vnode {
        if (options.length === 0) {
            return this.emptyState();
        }
        return m('div.filter-select-dropdown-items', this.itemsAttrs(), options.map((o, i) => this.item(o, i)));
    }

    private groupedList(groups: FilterGroup[]): Vnode {
        if (groups.length === 0) {
            return this.emptyState();
        }
        let flatIndex = 0;
        return m('div.filter-select-dropdown-items', this.itemsAttrs(), groups.map(g => {
            const startIndex = flatIndex;
            flatIndex += g.options.length;
            return this.group(g, startIndex);
        }));
    }

    private itemsAttrs() {
        return {
            key: '__items',
            oncreate: (vnode: VnodeDOM) => {
                this.itemsContainer = vnode.dom as HTMLElement;
            },
            onremove: () => {
                this.itemsContainer = null;
            },
        };
    }

    private group(group: FilterGroup, startIndex: number): Vnode {
        const children: Vnode[] = [];
        if (group.label !== '') {
            children.push(m('div.filter-select-dropdown-group-header', {
                key: `__group-header-${group.label}`,
            }, m('.fs-8.text-muted.fw-normal.border-bottom', {}, group.label)));
        }
        group.options.forEach((option, i) => {
            children.push(this.item(option, startIndex + i));
        });
        return m('div.filter-select-dropdown-group', {
            key: `__group-${group.label}`,
        }, children);
    }

    private emptyState(): Vnode {
        return m('div.filter-select-dropdown-empty', {
            key: '__empty',
        }, this.host.translator.translate('no_item_found'));
    }

    private search(): Vnode {
        return m('div.filter-select-dropdown-search', {
            key: '__search',
        }, [
            m('input.form-control', {
                type: 'text',
                value: this.host.searchQuery,
                oninput: (e: InputEvent) => {
                    this.host.setSearchQuery((e.target as HTMLInputElement).value);
                },
                onkeydown: (e: KeyboardEvent) => this.onSearchKeydown(e),
            }),
        ]);
    }

    private item(option: FilterOption, index: number): Vnode {
        const highlighted = index === this.host.highlightedIndex;
        const classes = [
            'filter-select-dropdown-item',
            option.selected ? 'is-selected' : '',
            highlighted ? 'is-highlighted' : '',
        ].filter(Boolean).join('.');

        return m(`button.${classes}`, {
            type: 'button',
            key: option.value,
            onclick: (e: MouseEvent) => {
                e.preventDefault();
                this.host.toggle(option);
            },
            onupdate: (vnode: VnodeDOM) => {
                if (highlighted) {
                    (vnode.dom as HTMLElement).scrollIntoView({ block: 'nearest' });
                }
            },
        }, [
            m('span.filter-select-dropdown-item-label', this.renderLabel(option)),
            option.selected
                ? m('i.bi.bi-check2.filter-select-dropdown-item-check.d-flex')
                : null,
        ]);
    }

    private onSearchKeydown(e: KeyboardEvent): void {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (this.host.getNavigableOptions().length === 0) {
                return;
            }
            e.preventDefault();
            this.host.moveHighlight(e.key === 'ArrowDown' ? 1 : -1);
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
        }
        if ((e.key === 'Enter' || e.key === ' ') && this.host.highlightedIndex >= 0) {
            e.preventDefault();
            const button = this.itemsContainer?.querySelectorAll<HTMLButtonElement>('.filter-select-dropdown-item')[this.host.highlightedIndex];
            button?.click();
        }
    }

    private renderLabel(option: FilterOption): Vnode | Vnode[] | string {
        if (this.host.displayMode !== 'stars') {
            return option.label;
        }

        const count = parseInt(option.label, 10);
        if (!Number.isFinite(count) || count <= 0) {
            return option.label;
        }

        const stars: Vnode[] = [];
        for (let i = 0; i < count; i++) {
            stars.push(m('i.bi.bi-star-fill.text-info' + (i > 0 ? '.ms-1' : '')));
        }
        return stars;
    }
}
